import { useCallback, useEffect, useRef, useState } from "react";
import { Check, MapPinned, Search, X } from "lucide-react";

import { api, type OrtTreffer, type Verlaufseintrag } from "@/api.ts";

interface Eigenschaften {
  eintrag: Verlaufseintrag;
  beiSpeichern: (aktualisiert: Verlaufseintrag) => void;
  beiAbbrechen: () => void;
}

export function SegmentBearbeiten({ eintrag, beiSpeichern, beiAbbrechen }: Eigenschaften) {
  const [label, setLabel] = useState(eintrag.beschriftung);
  const [suchwort, setSuchwort] = useState("");
  const [treffer, setTreffer] = useState<OrtTreffer[]>([]);
  const [suchOffen, setSuchOffen] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<OrtTreffer | null>(null);
  const suchRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const suchen = useCallback((wort: string) => {
    clearTimeout(timerRef.current);
    if (wort.trim().length < 2) {
      setTreffer([]);
      return;
    }
    timerRef.current = setTimeout(() => {
      void api.orte(wort, 10).then(setTreffer).catch(() => setTreffer([]));
    }, 250);
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const ortWaehlen = (ort: OrtTreffer) => {
    setGewaehlt(ort);
    setLabel(ort.name ?? "");
    setSuchOffen(false);
    setSuchwort("");
    setTreffer([]);
  };

  const speichern = async () => {
    setSpeichert(true);
    setFehler(null);
    try {
      const korrektur: { placeId?: string | null; lat?: number | null; lon?: number | null; label?: string | null } = {};

      if (gewaehlt) {
        korrektur.placeId = gewaehlt.placeId;
        if (gewaehlt.lat != null) korrektur.lat = gewaehlt.lat;
        if (gewaehlt.lon != null) korrektur.lon = gewaehlt.lon;
      }

      if (label !== eintrag.beschriftung) {
        korrektur.label = label;
      }

      if (Object.keys(korrektur).length === 0) {
        beiAbbrechen();
        return;
      }

      const aktualisiert = await api.segmentKorrigieren(eintrag.id, korrektur);
      beiSpeichern(aktualisiert);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="border-b border-border bg-page-bg px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-text-muted">Eintrag bearbeiten</span>
        <button onClick={beiAbbrechen} className="rounded p-1 hover:bg-surface" aria-label="Abbrechen">
          <X size={14} />
        </button>
      </div>

      <label className="mb-1 block text-xs text-text-muted">Bezeichnung</label>
      <input
        type="text"
        value={label}
        onChange={(e) => { setLabel(e.target.value); setGewaehlt(null); }}
        className="mb-2 w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-sm"
      />

      {eintrag.art === "visit" && (
        <>
          <button
            onClick={() => { setSuchOffen(!suchOffen); setTimeout(() => suchRef.current?.focus(), 50); }}
            className="mb-2 flex items-center gap-1.5 text-xs text-primary hover:text-primary-dark"
          >
            {suchOffen ? <X size={12} /> : <Search size={12} />}
            {suchOffen ? "Suche schließen" : "Anderen Ort suchen"}
          </button>

          {suchOffen && (
            <div className="relative mb-2">
              <input
                ref={suchRef}
                type="text"
                value={suchwort}
                placeholder="Ortsname eingeben…"
                onChange={(e) => { setSuchwort(e.target.value); suchen(e.target.value); }}
                className="w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-sm"
              />
              {treffer.length > 0 && (
                <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-border bg-surface shadow-lg">
                  {treffer.map((ort) => (
                    <li key={ort.placeId}>
                      <button
                        onClick={() => ortWaehlen(ort)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-page-bg"
                      >
                        <MapPinned size={14} className="shrink-0 text-text-muted" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{ort.name ?? ort.placeId}</span>
                          <span className="block text-xs text-text-muted">{ort.besuche} Besuche</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {gewaehlt && (
            <p className="mb-2 text-xs text-success">
              Ort wird auf „{gewaehlt.name ?? gewaehlt.placeId}" geändert
            </p>
          )}
        </>
      )}

      {fehler && <p className="mb-2 text-xs text-danger">{fehler}</p>}

      <div className="flex gap-2">
        <button
          onClick={() => void speichern()}
          disabled={speichert}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary-dark disabled:opacity-50"
        >
          <Check size={14} />
          {speichert ? "Speichert…" : "Speichern"}
        </button>
        <button
          onClick={beiAbbrechen}
          className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface"
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}
